import { useEffect, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import { getContactList } from '../../api/backendApi';

/**
 * ContactListViewerDialog
 *
 * Read-only viewer for a single contact list. Opened when a home screen
 * "Contact List" button is pressed.
 *
 * Props:
 *   open         {boolean}   — controls visibility
 *   onClose      {Function}  — called on close
 *   contactListId {string}   — the list to display
 *   tenantId     {string}    — tenant scope
 *
 * Future: once calling is enabled, a call action can be added here
 * without changing any data model or API.
 */
export default function ContactListViewerDialog({ open, onClose, contactListId, tenantId }) {
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open || !contactListId || !tenantId) {
      setList(null);
      setError('');
      return;
    }
    let active = true;
    setLoading(true);
    setError('');
    setList(null);

    getContactList(tenantId, contactListId)
      .then((resp) => {
        if (!active) return;
        const data = resp?.data?.contactList || resp?.contactList || resp;
        if (!data || !data.id) {
          setError('This contact list no longer exists.');
          return;
        }
        if (!data.active) {
          setError('This contact list is inactive.');
          return;
        }
        setList(data);
      })
      .catch((e) => {
        if (!active) return;
        const status = e?.status || e?.response?.status;
        if (status === 403 || status === 401) {
          setError('You do not have access to this contact list.');
        } else if (status === 404) {
          setError('This contact list no longer exists.');
        } else {
          setError('Failed to load contact list. Please try again.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [open, contactListId, tenantId]);

  const activeMembers = (list?.members || []).filter((m) => m.active !== false);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ pb: 0.5 }}>
        {loading ? 'Loading…' : (list?.name || 'Contact List')}
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="warning" sx={{ mt: 1 }}>{error}</Alert>
        )}

        {!loading && list && (
          <>
            {list.description ? (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                {list.description}
              </Typography>
            ) : null}

            <Divider sx={{ mb: 1 }} />

            <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
              Members
            </Typography>

            {activeMembers.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                No members configured.
              </Typography>
            ) : (
              <List disablePadding dense>
                {activeMembers
                  .sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0))
                  .map((member) => {
                    const primary = member.displayName || member.userId;
                    const secondary = member.email || null;
                    const initials = (primary || '?')
                      .split(/\s+/)
                      .slice(0, 2)
                      .map((p) => p[0])
                      .join('')
                      .toUpperCase();

                    return (
                      <ListItem key={member.id} disableGutters sx={{ py: 0.5 }}>
                        <ListItemAvatar sx={{ minWidth: 40 }}>
                          <Avatar sx={{ width: 32, height: 32, fontSize: 13 }}>
                            {initials || <PersonIcon fontSize="small" />}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={primary}
                          secondary={secondary}
                          primaryTypographyProps={{ variant: 'body2' }}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItem>
                    );
                  })}
              </List>
            )}

            <Divider sx={{ mt: 1.5, mb: 1 }} />

            <Typography
              variant="caption"
              color="text.disabled"
              sx={{ display: 'block', textAlign: 'center' }}
            >
              Calling not enabled yet
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
